import { baseApi } from './baseApi';
import type { ApiMessage } from './response';

/** The signed-in user's saved UI choices, keyed like `cols:board` (BE `user_preferences`). */
export type Preferences = Record<string, unknown>;

/** Notification types the user switched off in Settings (BE `notify:muted`). */
export const NOTIFY_MUTED_KEY = 'notify:muted';

const preferencesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    preferences: build.query<Preferences, undefined>({
      query: () => '/preferences',
      providesTags: ['Preference'],
      keepUnusedDataFor: 3600, // only this user writes them, and every write updates the cache
    }),
    savePreference: build.mutation<ApiMessage<unknown>, { key: string; value: unknown }>({
      query: ({ key, value }) => ({ url: `/preferences/${key}`, method: 'PUT', body: { value } }),
      // Muting a notification type changes what the Inbox lists and the bell counts.
      invalidatesTags: (_r, _e, { key }) =>
        key === NOTIFY_MUTED_KEY ? ['InboxSummary', 'Notification'] : [],
      // Show the choice at once; put the old one back if the server refuses it.
      async onQueryStarted({ key, value }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          preferencesApi.util.updateQueryData('preferences', undefined, (draft) => {
            draft[key] = value;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const { usePreferencesQuery, useSavePreferenceMutation } = preferencesApi;
